// Thin fetch wrapper used by every page. Always hits the same-origin /api
// prefix; Vite (dev) and nginx (prod) proxy it to the FastAPI backend.
//
// Responsibilities:
//   - inject Authorization: Bearer <token> when a token is in localStorage
//   - parse JSON bodies and surface structured ApiError for non-2xx responses
//   - on 401, clear the stored token and broadcast an "auth:expired" event so
//     AuthContext can react (set user=null and let ProtectedRoute redirect)

import { clearToken, getToken } from "../auth/storage";
import type { ValidationDetail } from "./types";

const API_BASE = "/api";

export const AUTH_EXPIRED_EVENT = "auth:expired";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;
  readonly code: string | null;
  readonly details: ValidationDetail[] | null;

  constructor(
    status: number,
    message: string,
    body: unknown,
    code: string | null,
    details: ValidationDetail[] | null,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
    this.code = code;
    this.details = details;
  }
}

export interface ApiRequestOptions extends Omit<RequestInit, "body"> {
  json?: unknown;
  // When set, the request body is the provided FormData and Content-Type is
  // left for the browser to fill in (so the multipart boundary is correct).
  // Used by file-upload flows (POST /api/batches).
  body?: BodyInit | null;
  // When true, the request will not auto-include the Authorization header
  // even if a token is present (used for login/signup so we never leak a
  // stale token in flows that explicitly mint a new one).
  skipAuth?: boolean;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function extractCode(body: unknown): string | null {
  if (isRecord(body) && typeof body.code === "string") return body.code;
  return null;
}

function extractMessage(body: unknown, fallback: string): string {
  if (isRecord(body)) {
    if (typeof body.message === "string") return body.message;
    if (typeof body.detail === "string") return body.detail;
  }
  return fallback;
}

function extractValidationDetails(body: unknown): ValidationDetail[] | null {
  if (!isRecord(body)) return null;
  const detail = body.detail;
  if (!Array.isArray(detail)) return null;
  const out: ValidationDetail[] = [];
  for (const entry of detail) {
    if (!isRecord(entry)) continue;
    const loc = entry.loc;
    const msg = entry.msg;
    const type = entry.type;
    if (Array.isArray(loc) && typeof msg === "string" && typeof type === "string") {
      out.push({
        loc: loc.filter(
          (p): p is string | number => typeof p === "string" || typeof p === "number",
        ),
        msg,
        type,
      });
    }
  }
  return out.length > 0 ? out : null;
}

export async function apiRequest<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  const { json, headers, skipAuth, body: rawBody, ...rest } = options;

  // For raw FormData / Blob bodies we must NOT set Content-Type ourselves -
  // the browser sets it (including the multipart boundary). For JSON we
  // always set application/json; everything else (e.g. POST with no body)
  // simply omits the header.
  const mergedHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(json !== undefined ? { "Content-Type": "application/json" } : {}),
  };
  if (headers) {
    // RequestInit allows several header shapes; normalise to a plain object.
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        mergedHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      for (const [key, value] of headers) {
        mergedHeaders[key] = value;
      }
    } else {
      Object.assign(mergedHeaders, headers as Record<string, string>);
    }
  }

  if (!skipAuth) {
    const token = getToken();
    if (token && !mergedHeaders.Authorization) {
      mergedHeaders.Authorization = `Bearer ${token}`;
    }
  }

  const init: RequestInit = {
    ...rest,
    headers: mergedHeaders,
  };
  if (json !== undefined) {
    init.body = JSON.stringify(json);
  } else if (rawBody !== undefined && rawBody !== null) {
    init.body = rawBody;
  }

  const url = path.startsWith("http") ? path : `${API_BASE}${path}`;
  const response = await fetch(url, init);

  // 204 No Content - return undefined cast as T (callers should expect void).
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("application/json")
    ? await response.json().catch(() => null)
    : await response.text().catch(() => null);

  if (!response.ok) {
    if (response.status === 401) {
      clearToken();
      if (typeof window !== "undefined") {
        window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT));
      }
    }
    const fallback = `Request failed with status ${response.status}`;
    const message = extractMessage(body, fallback);
    const code = extractCode(body);
    const details = extractValidationDetails(body);
    throw new ApiError(response.status, message, body, code, details);
  }

  return body as T;
}

export interface HealthResponse {
  status: string;
  service: string;
  version: string;
}

// Kept for the (optional) backend status probe used during early phases.
export const api = {
  health: () => apiRequest<HealthResponse>("/health"),
};
